import { fetchWithCsrf } from 'js/utils/fetch_with_csrf';
import type {
  TopicsResponse,
  TopicResponse,
  ResultsResponse,
  SuggestionsResponse,
  UnreadCountResponse,
  CreateTopicParams,
  UpdateTopicParams,
  ResultsTab,
} from '../types';

const BASE_PATH = '/v0/research_radar';
const API_PARAM = 'subdomain_param=api';

function apiUrl(path: string, extra?: URLSearchParams): string {
  const sep = extra && extra.toString() ? '&' : '';
  return `${path}?${API_PARAM}${sep}${extra?.toString() ?? ''}`;
}

export async function fetchTopics(): Promise<TopicsResponse> {
  const response = await fetch(apiUrl(`${BASE_PATH}/topics`));
  if (!response.ok) throw new Error('Failed to fetch topics');
  return response.json();
}

export async function fetchTopic(topicId: number): Promise<TopicResponse> {
  const response = await fetch(apiUrl(`${BASE_PATH}/topics/${topicId}`));
  if (!response.ok) throw new Error('Failed to fetch topic');
  return response.json();
}

export async function createTopic(
  params: CreateTopicParams,
): Promise<TopicResponse> {
  const response = await fetchWithCsrf(apiUrl(`${BASE_PATH}/topics`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.errors?.join(', ') || 'Failed to create topic');
  }
  return response.json();
}

export async function updateTopic(
  topicId: number,
  params: UpdateTopicParams,
): Promise<TopicResponse> {
  const response = await fetchWithCsrf(
    apiUrl(`${BASE_PATH}/topics/${topicId}`),
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    },
  );
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.errors?.join(', ') || 'Failed to update topic');
  }
  return response.json();
}

export async function deleteTopic(topicId: number): Promise<void> {
  const response = await fetchWithCsrf(
    apiUrl(`${BASE_PATH}/topics/${topicId}`),
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    },
  );
  if (!response.ok) throw new Error('Failed to delete topic');
}

export async function fetchResults(
  topicId: number,
  tab: ResultsTab = 'new',
  page = 1,
): Promise<ResultsResponse> {
  const params = new URLSearchParams({
    tab,
    page: String(page),
  });
  const response = await fetch(
    apiUrl(`${BASE_PATH}/topics/${topicId}/results`, params),
  );
  if (!response.ok) throw new Error('Failed to fetch results');
  return response.json();
}

export async function markResultsRead(
  topicId: number,
  resultIds: number[],
): Promise<void> {
  const response = await fetchWithCsrf(
    apiUrl(`${BASE_PATH}/topics/${topicId}/mark_read`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ result_ids: resultIds }),
    },
  );
  if (!response.ok) throw new Error('Failed to mark results as read');
}

export async function markAllResultsRead(topicId: number): Promise<void> {
  const response = await fetchWithCsrf(
    apiUrl(`${BASE_PATH}/topics/${topicId}/mark_all_read`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    },
  );
  if (!response.ok) throw new Error('Failed to mark all results as read');
}

export async function fetchSuggestions(): Promise<SuggestionsResponse> {
  const response = await fetch(apiUrl(`${BASE_PATH}/suggestions`));
  if (!response.ok) throw new Error('Failed to fetch suggestions');
  return response.json();
}

export async function fetchUnreadCount(): Promise<UnreadCountResponse> {
  const response = await fetch(apiUrl(`${BASE_PATH}/unread_count`));
  if (!response.ok) throw new Error('Failed to fetch unread count');
  return response.json();
}

export interface ReadingHistoryWork {
  id: number;
  title: string;
  year: number | null;
  journal: string | null;
  thumbnail_url: string | null;
}

export interface ReadingHistoryResponse {
  works: ReadingHistoryWork[];
}

export async function fetchReadingHistory(
  query = '',
): Promise<ReadingHistoryResponse> {
  const params = new URLSearchParams();
  if (query) params.set('query', query);
  const response = await fetch(apiUrl(`${BASE_PATH}/reading_history`, params));
  if (!response.ok) throw new Error('Failed to fetch reading history');
  return response.json();
}

export interface FeatureEnabledResponse {
  enabled: boolean;
}

let featureEnabledCache: boolean | null = null;

export async function fetchFeatureEnabled(): Promise<boolean> {
  if (featureEnabledCache !== null) return featureEnabledCache;
  try {
    const response = await fetch(apiUrl(`${BASE_PATH}/feature_enabled`));
    if (!response.ok) {
      featureEnabledCache = false;
      return false;
    }
    const data: FeatureEnabledResponse = await response.json();
    featureEnabledCache = data.enabled === true;
    return featureEnabledCache;
  } catch {
    featureEnabledCache = false;
    return false;
  }
}

export async function forceGenerate(
  topicId: number,
): Promise<{ success: boolean; message: string }> {
  const response = await fetchWithCsrf(
    apiUrl(`${BASE_PATH}/topics/${topicId}/force_generate`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    },
  );
  if (!response.ok) throw new Error('Failed to force generate');
  return response.json();
}

export async function markResultsDownloaded(topicId: number): Promise<void> {
  const response = await fetchWithCsrf(
    apiUrl(`${BASE_PATH}/topics/${topicId}/mark_downloaded`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    },
  );
  if (!response.ok) throw new Error('Failed to mark results as downloaded');
}
